export const FOG_VISIBILITY_STATE = Object.freeze({
  HIDDEN: "hidden",
  REMEMBERED: "remembered",
  CURRENT: "current",
});

export const FOG_OF_WAR_SETTINGS = Object.freeze({
  gridCellSizeMeters: 30,
  maxRenderedCells: 5000,
  fogViewportPaddingMeters: 220,
  minMapZoom: 13.2,
  starterEdgePaddingMeters: 90,
  openingVisionRadiusMeters: 120,
  // The Agency Update: base vision is deliberately tight. Watchers posted on
  // missions are the player's real eyes; the base only sees its own block.
  baseVisionRadiusMeters: 140,
  buildingClearVisionRadiusMeters: 70,
  scoutVisionRadiusMeters: 55,
});

export const DEV_SETTINGS_STORAGE_KEY = "apocalypse-dispatch.v4.dev-settings";

export const DEFAULT_DEV_SETTINGS = Object.freeze({
  supplyRun: Object.freeze({
    moveMetersPerTurn: 60,
    offStreetMultiplier: 2,
    sightMeters: 70,
    coneDegrees: 80,
    // Starting enemy force: guards hold intersections, patrollers walk a street
    // back and forth (see tactical-enemy-ai.mjs). Threat still adds reinforcements
    // on top during the supply run.
    guardCount: 2,
    patrollerCount: 1,
  }),
  baseDefense: Object.freeze({
    initiative: "survivors-first",
    attackerCap: 4,
    doorHitPoints: 0,
    approachMetersPerSecond: 28,
    hopMs: 550,
    roundMs: 650,
    breachPauseMs: 350,
  }),
});

export const DEV_SETTING_SCHEMA = Object.freeze({
  supplyRun: Object.freeze({
    moveMetersPerTurn: Object.freeze({ min: 20, max: 140, step: 5 }),
    offStreetMultiplier: Object.freeze({ min: 1, max: 8, step: 0.25 }),
    sightMeters: Object.freeze({ min: 20, max: 160, step: 5 }),
    coneDegrees: Object.freeze({ min: 20, max: 180, step: 5 }),
    guardCount: Object.freeze({ min: 0, max: 8, step: 1 }),
    patrollerCount: Object.freeze({ min: 0, max: 8, step: 1 }),
  }),
  baseDefense: Object.freeze({
    initiative: Object.freeze({ choices: Object.freeze(["survivors-first", "enemies-first"]) }),
    attackerCap: Object.freeze({ min: 1, max: 12, step: 1 }),
    doorHitPoints: Object.freeze({ min: 0, max: 12, step: 1 }),
    approachMetersPerSecond: Object.freeze({ min: 8, max: 80, step: 1 }),
    hopMs: Object.freeze({ min: 120, max: 1600, step: 10 }),
    roundMs: Object.freeze({ min: 120, max: 2400, step: 10 }),
    breachPauseMs: Object.freeze({ min: 0, max: 1800, step: 10 }),
  }),
});

function clampNumber(value, schema, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(schema.min, Math.min(schema.max, numeric));
}

export function sanitizeDevSettings(raw) {
  const next = {
    supplyRun: { ...DEFAULT_DEV_SETTINGS.supplyRun },
    baseDefense: { ...DEFAULT_DEV_SETTINGS.baseDefense },
  };
  const input = raw && typeof raw === "object" ? raw : {};
  for (const key of Object.keys(next.supplyRun)) {
    next.supplyRun[key] = clampNumber(input.supplyRun?.[key], DEV_SETTING_SCHEMA.supplyRun[key], DEFAULT_DEV_SETTINGS.supplyRun[key]);
  }
  for (const key of Object.keys(next.baseDefense)) {
    if (key === "initiative") {
      next.baseDefense.initiative = DEV_SETTING_SCHEMA.baseDefense.initiative.choices.includes(input.baseDefense?.initiative)
        ? input.baseDefense.initiative
        : DEFAULT_DEV_SETTINGS.baseDefense.initiative;
    } else {
      next.baseDefense[key] = clampNumber(input.baseDefense?.[key], DEV_SETTING_SCHEMA.baseDefense[key], DEFAULT_DEV_SETTINGS.baseDefense[key]);
    }
  }
  return next;
}

export function loadDevSettings(storage = globalThis.localStorage) {
  try {
    return sanitizeDevSettings(JSON.parse(storage?.getItem(DEV_SETTINGS_STORAGE_KEY) || "null"));
  } catch {
    return sanitizeDevSettings(null);
  }
}

export function saveDevSettings(settings, storage = globalThis.localStorage) {
  const sanitized = sanitizeDevSettings(settings);
  try {
    storage?.setItem(DEV_SETTINGS_STORAGE_KEY, JSON.stringify(sanitized));
  } catch {
    // Settings are developer-only; failing to persist should not affect a run.
  }
  return sanitized;
}
